/**
 * Server functions for coach-side image uploads (S3 / Cloudflare R2).
 *
 * Coach-facing equivalent of `getCihUploadUrlFn` — auth-gated by the logged-in
 * coach's Clerk session. Returns the presigned PUT URL plus the resolved
 * public URL so the caller can store the URL directly in the DB column
 * (compatible with the existing `UrlSchema` validation and downstream
 * consumers that expect full URLs).
 *
 * Aliases: coach upload, coach image, presigned url, s3 upload, hero image upload
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import { S3Middleware } from "~/lib/s3-middleware";
import {
  COACH_IMAGE_EXTENSIONS,
  type CoachImageMimeType,
  coachImageMimeTypeSchema,
  type CoachImagePurpose,
  coachImagePurposeSchema,
} from "~/lib/shared/coach-image-types";

const GetCoachImageUploadUrlSchema = z.object({
  purpose: coachImagePurposeSchema,
  fileMimeType: coachImageMimeTypeSchema,
});

function buildCoachImageKey(args: {
  coachId: string;
  purpose: CoachImagePurpose;
  mimeType: CoachImageMimeType;
}): string {
  const uniqueSuffix = Date.now().toString(36);
  return `coach/${args.coachId}/${args.purpose}-${uniqueSuffix}.${COACH_IMAGE_EXTENSIONS[args.mimeType]}`;
}

/**
 * Generate a presigned upload URL for a coach profile image.
 * Returns the upload URL, the S3 key, and the resolved public URL.
 * Callers should store the `publicUrl` in the DB column.
 */
export const getCoachImageUploadUrlFn = createServerFn({ method: "POST" })
  .middleware([S3Middleware, requireClerkCoachAuth])
  .validator(GetCoachImageUploadUrlSchema)
  .handler(async ({ data, context }) => {
    const { getPresignedUploadUrl, getPublicUrl } =
      await import("~/lib/server/s3");

    const key = buildCoachImageKey({
      coachId: context.coach.id,
      purpose: data.purpose,
      mimeType: data.fileMimeType,
    });

    const { url } = await getPresignedUploadUrl({
      key,
      contentType: data.fileMimeType,
    });

    return { uploadUrl: url, key, publicUrl: getPublicUrl(key) };
  });
